import React, { useMemo } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import {
    Box,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import DataGridScrollTable from "src/v2/components/Table/data-grid-scroll-table"
import StatusBadge from "src/v2/components/StatusBadge"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as DownloadIcon } from "src/v2/assets/icons/download-icon.svg"
import { getStatusView } from "../constants"

const formatDateTime = (value) => {
    if (!value) return "-"
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value
}

const formatDate = (value) => {
    if (!value) return "-"
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value
}

const formatDuration = (seconds) => {
    if (!seconds) return "-"
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`
}

const StatusCell = ({ status }) => {
    const statusView = getStatusView(status)

    return (
        <Stack spacing={0.8} sx={{ width: "100%" }}>
            <StatusBadge
                label={statusView.label}
                backgroundColor={statusView.color}
                radius={1}
            />
            <LinearProgress
                variant="determinate"
                value={statusView.progress}
                sx={{
                    height: 5,
                    borderRadius: 1,
                    backgroundColor: "rgba(255,255,255,0.18)",
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: statusView.color,
                    },
                }}
            />
        </Stack>
    )
}

StatusCell.propTypes = {
    status: PropTypes.string,
}

const WorkflowRunsTable = ({
    rows,
    loading,
    workflowType,
    onDownload,
    onRefresh,
}) => {
    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: "workflow_label",
                headerName: "Workflow",
                minWidth: 120,
                flex: 0.5,
                renderCell: (params) => (
                    <Typography variant="body2" fontWeight={700} color="common.white">
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "status",
                headerName: "Status",
                minWidth: 170,
                flex: 0.8,
                renderCell: (params) => <StatusCell status={params.value} />,
            },
            {
                field: "periodo",
                headerName: "Período",
                minWidth: 190,
                flex: 0.75,
                valueGetter: (params) => `${formatDate(params.row.data_inicial)} a ${formatDate(params.row.data_final)}`,
            },
            {
                field: "criado_em",
                headerName: "Criado em",
                minWidth: 150,
                flex: 0.6,
                valueGetter: (params) => formatDateTime(params.value),
            },
            {
                field: "nome_usuario",
                headerName: "Usuário",
                minWidth: 150,
                flex: 0.65,
                valueGetter: (params) => params.value || params.row.criado_por || "-",
            },
            {
                field: "duration_seconds",
                headerName: "Duração",
                minWidth: 110,
                flex: 0.4,
                valueGetter: (params) => formatDuration(params.value),
            },
            {
                field: "outputs",
                headerName: "Saídas",
                minWidth: 220,
                flex: 1,
                sortable: false,
                renderCell: (params) => {
                    const isDone = params.row.status === "REPORT_DONE"
                    const outputs = params.value || []

                    return (
                        <Stack direction="row" spacing={1} sx={{ overflow: "hidden" }}>
                            {outputs.map((output) => (
                                <Tooltip title={output.filename} key={output.filename}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!isDone}
                                            onClick={() => onDownload(output.download_endpoint)}
                                            sx={{
                                                color: "common.white",
                                                border: "1px solid rgba(255,255,255,0.45)",
                                                width: 34,
                                                height: 34,
                                                "&.Mui-disabled": {
                                                    color: "rgba(255,255,255,0.35)",
                                                },
                                                "& svg": {
                                                    color: "inherit",
                                                },
                                            }}
                                        >
                                            <DownloadIcon width={18} height={18} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ))}
                        </Stack>
                    )
                },
            },
        ]

        if (workflowType !== "if") return baseColumns

        return [
            baseColumns[0],
            {
                field: "use_gpu",
                headerName: "GPU",
                minWidth: 80,
                flex: 0.28,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const enabled = Boolean(params.value)
                    return (
                        <Tooltip title={enabled ? "Executado com GPU" : "Executado sem GPU"}>
                            <MemoryRoundedIcon
                                fontSize="small"
                                sx={{
                                    color: enabled ? "icon.main" : "icon.disabled",
                                }}
                            />
                        </Tooltip>
                    )
                },
            },
            ...baseColumns.slice(1),
        ]
    }, [onDownload, workflowType])

    return (
        <Paper
            sx={{
                flex: 1,
                p: 3,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                height: "calc(100vh - 360px)",
                backgroundColor: "background.paper",
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Typography variant="h3" color="common.white">
                    Últimas Execuções
                </Typography>

                <ButtonWithIcon
                    icon={<RefreshRoundedIcon />}
                    onClick={onRefresh}
                    disabled={loading}
                >
                    ATUALIZAR
                </ButtonWithIcon>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGridScrollTable
                    key={workflowType}
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowHeight={64}
                    sx={{
                        "& .MuiDataGrid-virtualScroller": {
                            minHeight: "260px",
                        },
                    }}
                />
            </Box>
        </Paper>
    )
}

WorkflowRunsTable.propTypes = {
    rows: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    workflowType: PropTypes.string.isRequired,
    onDownload: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
}

export default WorkflowRunsTable
